/**
 * assignment.v1 cizici — ogeleri bir tabloya (satir x sutun) SURUKLEYEREK yerlestirme.
 *
 * Sozlesme (istemci-temiz icerik; dogru hucre BURADA YOK — sabit ornek disinda):
 *   - rows[]    : tablo satirlari  { id, label, color? }   (or. renk)
 *   - columns[] : tablo sutunlari  { id, label }           (or. tur)
 *   - items[]   : suruklenebilir ogeler { id, imageUrl, fixed?, row?, col? }
 *       fixed=true ogeler onceden yerlesik ORNEK'tir (row/col gorunur, tasinamaz);
 *       digerlerinde row/col SILINMISTIR (dogru cevap istemciye gelmez).
 *   - cevap     : onAnswerChange({ placements: { <itemId>: "<rowId>:<colId>" } });
 *                 sabit-olmayan her oge yerlesene kadar null (ileri kapali).
 *
 * Surukleme pointer-tabanli (fare + dokunma). Yerlesik ogeye tekrar dokunup baska
 * hucreye/havuza tasinabilir. Skorlama SUNUCUDA (renk satiri x tur sutunu eslesmesi).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MechanicProps } from "./types";
import { sesiGuvenliOynat } from "./audioSafety";

interface ARow {
  id: string;
  label: string;
  color?: string;
}
interface ACol {
  id: string;
  label: string;
}
interface AItem {
  id: string;
  imageUrl?: string;
  fixed?: boolean;
  row?: string;
  col?: string;
}
interface AContent {
  prompt?: { text?: string; subText?: string; gateText?: string };
  rows?: ARow[];
  columns?: ACol[];
  items?: AItem[];
  /** true: sesli soru iskeleti — ust tarafta "Sesi oynat" yer tutucu buton. */
  audioButton?: boolean;
  /** ses dosyasi (varsa buton oynatir). */
  audioUrl?: string;
  /** true: tablo yerine BASLIKSIZ/IZGARASIZ ayri kutular (s36 sirali yerlestirme). */
  slotsRow?: boolean;
  /** true: kutular/havuz SES BITENE KADAR gizli; once "Sesi oynat" + gateText gosterilir. */
  audioGate?: boolean;
}

const cellId = (r: string, c: string) => `${r}:${c}`;

export function AssignmentItem({ item, onAnswerChange }: MechanicProps) {
  const content = item.content as unknown as AContent;
  const rows = content.rows ?? [];
  const cols = content.columns ?? [];
  const items = content.items ?? [];
  const byId: Record<string, AItem> = {};
  items.forEach((i) => (byId[i.id] = i));

  const initial = () => {
    const p: Record<string, string> = {};
    items.forEach((i) => {
      if (i.fixed && i.row && i.col) p[i.id] = cellId(i.row, i.col);
    });
    const saved = item.answer?.placements as Record<string, string> | undefined;
    if (saved) Object.assign(p, saved);
    return p;
  };
  const [placements, setPlacements] = useState<Record<string, string>>(initial);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDone, setAudioDone] = useState(false);
  // ses-kapisi: kutular/havuz SES BITENE kadar gizli (once dinle-yonergesi + oynat)
  const audioGate = !!content.audioGate && !!content.audioUrl;
  const showContent = !audioGate || audioDone;

  const audio = useRef<HTMLAudioElement | null>(null);
  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const nonFixed = items.filter((i) => !i.fixed);
    const totalCells = rows.length * cols.length;
    const placed = nonFixed.filter((i) => placements[i.id]);
    // token > slot ise (s36 sıralama): tüm slotlar dolunca biter.
    // klasik atama (token == hücre): tüm ögeler yerleşince biter.
    const done =
      items.length > totalCells
        ? new Set(placed.map((i) => placements[i.id])).size >= totalCells
        : nonFixed.length > 0 && placed.length === nonFixed.length;
    if (done) {
      const out: Record<string, string> = {};
      placed.forEach((i) => (out[i.id] = placements[i.id]!));
      cb.current({ placements: out });
    } else {
      const out: Record<string, string> = {};
      placed.forEach((i) => (out[i.id] = placements[i.id]!));
      cb.current(null, placed.length > 0 ? { placements: out } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements]);

  // hangi oge hangi hucrede
  const occupantOf = (cid: string) =>
    Object.keys(placements).find((k) => placements[k] === cid);

  function place(itemId: string, cid: string) {
    setPlacements((prev) => {
      const next = { ...prev };
      const occ = Object.keys(next).find((k) => next[k] === cid);
      if (occ && occ !== itemId) {
        if (byId[occ]?.fixed) return prev; // sabit ornegin uzerine konulamaz
        delete next[occ]; // eski sakin havuza doner
      }
      next[itemId] = cid;
      return next;
    });
  }
  function unplace(itemId: string) {
    if (byId[itemId]?.fixed) return;
    setPlacements((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }

  // pointer surukleme
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) =>
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    const up = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const cellEl = el?.closest("[data-cell]") as HTMLElement | null;
      const poolEl = el?.closest("[data-pool]") as HTMLElement | null;
      if (cellEl) place(drag.id, cellEl.dataset.cell!);
      else if (poolEl) unplace(drag.id);
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  function startDrag(e: React.PointerEvent, id: string) {
    if (byId[id]?.fixed) return;
    e.preventDefault();
    setDrag({ id, x: e.clientX, y: e.clientY });
  }

  const poolItems = items.filter((i) => !i.fixed && !placements[i.id]);
  const dragItem = drag ? byId[drag.id] : null;

  const titleText = showContent
    ? content.prompt?.text
    : content.prompt?.gateText ?? content.prompt?.text;

  return (
    <div className="mech assign-mech">
      {titleText && <h2 className="prompt">{titleText}</h2>}

      {(content.audioButton || content.audioUrl) && (!audioGate || !audioDone) && <hr className="q-divider" />}
      {(content.audioButton || content.audioUrl) && (!audioGate || !audioDone) && (
        <div className="center-action" style={{ margin: audioGate ? "18px 0" : "4px 0 12px" }}>
          {audioPlaying ? (
            <div className="waveform" aria-label="ses çalıyor">
              <span /><span /><span /><span /><span />
            </div>
          ) : (
            <button
              type="button"
              className="big-action-btn"
              disabled={!content.audioUrl}
              title={content.audioUrl ? "" : "ses sonra eklenecek"}
              onClick={() => {
                setAudioPlaying(true);
                // ses yok/oynatilamiyor/takildiysa kapiyi yine de ac
                sesiGuvenliOynat(audio.current, () => { setAudioPlaying(false); if (audioGate) setAudioDone(true); });
              }}
            >
              ▶ Sesi oynat
            </button>
          )}
          {content.audioUrl && (
            <audio
              ref={audio}
              src={content.audioUrl}
              preload="auto"
              onEnded={() => { setAudioPlaying(false); if (audioGate) setAudioDone(true); }}
              onError={() => { if (audioGate) setAudioDone(true); }}
            />
          )}
        </div>
      )}

      {showContent && (
        <>
          {content.slotsRow ? (
        <div className="assign-slots">
          {cols.map((c) => {
            const cid = cellId(rows[0]?.id ?? "r1", c.id);
            const occ = occupantOf(cid);
            const it = occ ? byId[occ] : null;
            return (
              <div key={c.id} className="assign-slot" data-cell={cid}>
                {it?.imageUrl && (
                  <img
                    src={it.imageUrl}
                    alt=""
                    draggable={false}
                    className={`assign-placed${drag?.id === it.id ? " dragging" : ""}`}
                    onPointerDown={(e) => startDrag(e, it.id)}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="assign-table" style={{ gridTemplateColumns: `auto repeat(${cols.length}, 1fr)` }}>
          <div className="assign-corner" />
          {cols.map((c) => (
            <div key={c.id} className="assign-colhead">
              {c.label}
            </div>
          ))}
          {rows.map((r) => (
            <FragmentRow
              key={r.id}
              r={r}
              cols={cols}
              occupantOf={occupantOf}
              byId={byId}
              draggingId={drag?.id ?? null}
              onGrab={startDrag}
            />
          ))}
        </div>
      )}

      <p className="assign-hint">
        {content.prompt?.subText || "Görselleri sürükleyip doğru kutuya bırak."}
      </p>

      <div className="assign-pool" data-pool="1">
        {poolItems.map((i) => (
          <button
            key={i.id}
            type="button"
            className={`assign-chip${drag?.id === i.id ? " dragging" : ""}`}
            onPointerDown={(e) => startDrag(e, i.id)}
          >
            {i.imageUrl && <img src={i.imageUrl} alt="" draggable={false} />}
          </button>
        ))}
      </div>
        </>
      )}

      {/* suruklenen hayalet: body'ye cizilir. Kisa ekranda soru alani zoom ile kucultulunce
          position:fixed koordinatlari da olcekleniyor, hayalet imlecten kayiyordu (3. sinif s36). */}
      {drag && dragItem?.imageUrl && createPortal(
        <img
          className="assign-ghost"
          src={dragItem.imageUrl}
          alt=""
          draggable={false}
          style={{ left: drag.x, top: drag.y }}
        />,
        document.body
      )}
    </div>
  );
}

function FragmentRow({
  r,
  cols,
  occupantOf,
  byId,
  draggingId,
  onGrab,
}: {
  r: ARow;
  cols: ACol[];
  occupantOf: (cid: string) => string | undefined;
  byId: Record<string, AItem>;
  draggingId: string | null;
  onGrab: (e: React.PointerEvent, id: string) => void;
}) {
  return (
    <>
      <div className="assign-rowhead" style={r.color ? { color: r.color } : undefined}>
        {r.label}
      </div>
      {cols.map((c) => {
        const cid = cellId(r.id, c.id);
        const occ = occupantOf(cid);
        const it = occ ? byId[occ] : null;
        return (
          <div key={c.id} className="assign-cell" data-cell={cid}>
            {it?.imageUrl && (
              <img
                src={it.imageUrl}
                alt=""
                draggable={false}
                className={`assign-placed${it.fixed ? " fixed" : ""}${draggingId === it.id ? " dragging" : ""}`}
                onPointerDown={(e) => onGrab(e, it.id)}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
